import { apiFiles } from "@/api/file.api.ts";
import { FileItem } from "@/components/files/FileItem.tsx";
import { FileUpload } from "@/components/files/FileUpload.tsx";
import { RichEditor } from "@/components/rich-editor/RichEditor.tsx";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { useMentionsHook } from "@/hooks/use-mention.hook.ts";
import {
  useApplicantActionCreate,
  useApplicantActionUpdate,
} from "@/queries/recruit/use-applicant-actions.query.ts";
import {
  useCandidateActionCreate,
  useCandidateActionUpdate,
} from "@/queries/recruit/use-candidate-actions.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiAction, ApiActionPayload, ApiActionVisibilities } from "@/types/action.types.ts";
import { ApiEntityTypes, ApiFile } from "@/types/common.types.ts";
import { Box, Button, FontAwesomeIcon, Stack } from "@skailer-com/uikit";
import { Editor } from "@tiptap/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface AddCommentProps {
  entityId: number | undefined;
  entityType: ApiEntityTypes;
  action?: ApiAction;
  onSaved?: () => void;
  save?: (payload: ApiActionPayload, action?: ApiAction) => Promise<boolean>;
  withFiles?: boolean;
  withEmoji?: boolean;
  isCollapsedOnInit?: boolean;
  label?: string;
  submitLabel?: string;
  hideControlsIfEmptyText?: boolean;
  initValue?: string;
  draftId?: string;
  initialFiles?: ApiFile[];
  onCancel?: () => void;
  onFileUploaded?: (file: ApiFile) => void;
  onFileRemove?: (file: ApiFile) => Promise<boolean>;
  onFileOpen?: (file: ApiFile) => Promise<void>;
  isFileOpenDisabled?: (file: ApiFile) => boolean;
  onFilesSaved?: (files: ApiFile[]) => Promise<void>;
  onFilesCancel?: () => Promise<void>;
  showControlsWhenEmpty?: boolean;
}

const readDraft = (draftId: string | undefined) => {
  if (!draftId) return null;

  try {
    return localStorage.getItem(`draft_${draftId}`);
  } catch {
    return null;
  }
};

export const AddComment = ({
  entityId,
  entityType,
  action,
  onSaved,
  save,
  withFiles = true,
  withEmoji = true,
  isCollapsedOnInit = true,
  initValue,
  label,
  submitLabel,
  hideControlsIfEmptyText = false,
  draftId,
  initialFiles,
  onCancel,
  onFileUploaded,
  onFileRemove,
  onFileOpen,
  isFileOpenDisabled,
  onFilesSaved,
  onFilesCancel,
  showControlsWhenEmpty = false,
}: AddCommentProps) => {
  const [isCollapsed, setIsCollapsed] = useState(isCollapsedOnInit);
  const { t } = useTranslation();
  const { replaceMentions } = useMentionsHook();
  const [text, setText] = useState(
    () =>
      (!initValue && readDraft(draftId)) ||
      (initValue ??
        (replaceMentions(action?.comment_data?.text, action?.comment_data?.mentioned_employees) ||
          ""))
  );
  const [syncedDraftId, setSyncedDraftId] = useState(draftId);

  if (draftId !== syncedDraftId) {
    setSyncedDraftId(draftId);
    const draft = initValue ? null : readDraft(draftId);
    if (draft) setText(draft);
  }

  const [originalFiles] = useState<ApiFile[]>(initialFiles ?? action?.comment_data?.files ?? []);
  const [files, setFiles] = useState<ApiFile[]>(originalFiles);
  const { handleErrors } = useErrorHandle();
  const [isPending, setIsPending] = useState<boolean>(false);
  const editorRef = useRef<Editor | null>(null);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed(!isCollapsed);

    setTimeout(() => {
      editorRef.current?.commands.focus();
    }, 200);
  }, [isCollapsed]);

  const candidateActionCreate = useCandidateActionCreate();
  const applicantActionCreate = useApplicantActionCreate();

  const candidateActionUpdate = useCandidateActionUpdate();
  const applicantActionUpdate = useApplicantActionUpdate();

  const saveDraft = useCallback(() => {
    if (!draftId || !text.trim()) return;

    try {
      localStorage.setItem(`draft_${draftId}`, text);
    } catch {
      // localStorage недоступен
    }
  }, [draftId, text]);

  const clearDraft = useCallback(() => {
    if (!draftId) return;

    try {
      localStorage.removeItem(`draft_${draftId}`);
    } catch {
      // игнорируем
    }
  }, [draftId]);

  useEffect(() => {
    if (!text.trim()) return;

    const timeout = setTimeout(saveDraft, 1000);
    return () => clearTimeout(timeout);
  }, [text, saveDraft]);

  const handleSave = useCallback(
    async (overrideText?: string) => {
      if (!entityId) return;

      const currentText = overrideText ?? text;

      if (files.length === 0 && !currentText.trim()) {
        // setError("text", { type: "manual", message: t("errors:field_required") });
        // todo show error
        return;
      }

      const payload: ApiActionPayload = {
        comment: currentText,
        file_uuids: files.map((f) => f.uuid),
        visibility: ApiActionVisibilities.public,
      };

      if (save) {
        setIsPending(true);
        const success = await save(payload, action);

        if (success) {
          await onFilesSaved?.(files);
          setText("");
          setFiles([]);
          if (onSaved) onSaved();
        }

        setIsPending(false);

        return;
      }

      if (action) {
        if (entityType == ApiEntityTypes.candidate) {
          candidateActionUpdate.mutate(
            {
              id: action.item_id,
              actionId: action.id,
              payload,
            },
            {
              onSuccess: () => {
                analyticsService.trackEvent(analyticEvents.candidates.actionEdited, {
                  [analyticProperties.id]: action.item_id,
                });
                onSaved?.();
              },
            }
          );
        } else {
          applicantActionUpdate.mutate(
            {
              id: action.item_id,
              actionId: action.id,
              payload,
            },
            {
              onSuccess: () => {
                analyticsService.trackEvent(analyticEvents.applicants.actionEdited, {
                  [analyticProperties.id]: action.item_id,
                });
                onSaved?.();
              },
            }
          );
        }
      } else {
        if (entityType == ApiEntityTypes.candidate) {
          candidateActionCreate.mutate(
            { id: entityId, payload },
            {
              onSuccess: () => {
                setText("");
                setFiles([]);
                clearDraft();

                analyticsService.trackEvent(analyticEvents.candidates.addedAction, {
                  [analyticProperties.id]: entityId,
                  [analyticProperties.actionType]: "Comment Added",
                  [analyticProperties.source]: "App",
                });
                onSaved?.();
              },
            }
          );
        } else {
          applicantActionCreate.mutate(
            { id: entityId, payload },
            {
              onSuccess: () => {
                setText("");
                setFiles([]);
                clearDraft();

                analyticsService.trackEvent(analyticEvents.applicants.addedAction, {
                  [analyticProperties.id]: entityId,
                  [analyticProperties.actionType]: "Comment Added",
                  [analyticProperties.source]: "App",
                });
                onSaved?.();
              },
            }
          );
        }
      }
    },
    [entityId, entityType, text, files, action]
  );

  async function removeFile(file: ApiFile) {
    if (onFileRemove) {
      const removed = await onFileRemove(file);
      if (!removed) return;
      setFiles((current) => current.filter((item) => item.uuid !== file.uuid));
      return;
    }

    setFiles((current) => current.filter((item) => item.id !== file.id));

    const { error } = await apiFiles.delete(file.uuid);
    if (error) handleErrors(error);
  }

  const handleCancel = async () => {
    await onFilesCancel?.();
    setFiles(originalFiles);
    setText(
      initValue ??
        (replaceMentions(action?.comment_data?.text, action?.comment_data?.mentioned_employees) ||
          "")
    );
    onCancel?.();
  };

  const handleSaveFromEditor = useCallback(
    (editor: Editor) => {
      const currentText = editor.getHTML();
      handleSave(currentText);
    },
    [files, entityId, entityType, action]
  );

  if (isCollapsed) {
    return (
      <Box>
        <Button size={"sm"} variant={"secondary"} onClick={toggleCollapsed}>
          {t("common:add_comment") + "..."}
        </Button>
      </Box>
    );
  }

  return (
    <Stack gap={"sm"}>
      <RichEditor
        value={text}
        hideToolbar={true}
        label={label ? label : t("common:comments")}
        likeTextArea={true}
        highlightMentions={true}
        insertEmojiIsOn={withEmoji}
        ref={editorRef}
        onChange={(value) => setText(value)}
        onSave={handleSaveFromEditor}
      />
      {files.length > 0 && (
        <Stack gap={"sm"}>
          {files.map((f) => (
            <FileItem
              key={`file-${f.id}`}
              file={f}
              onOpenFile={onFileOpen}
              isOpenDisabled={isFileOpenDisabled?.(f)}
              onRemove={() => {
                void removeFile(f);
              }}
            />
          ))}
        </Stack>
      )}
      {(!hideControlsIfEmptyText ||
        text.length > 0 ||
        files.length > 0 ||
        showControlsWhenEmpty) && (
        <Stack items={"center"} justify={"between"} direction={"horizontal"} gap={"sm"}>
          {withFiles ? (
            <Box>
              <FileUpload
                entityId={entityId}
                entityType={entityType}
                multiple
                afterSend={(file) => {
                  if (file) {
                    onFileUploaded?.(file);
                    setFiles((current) => [...current, file]);
                  }
                }}
              />
            </Box>
          ) : (
            <div></div>
          )}

          <Stack direction={"horizontal"} gap={"sm"}>
            {onCancel && (
              <Button variant="secondary" onClick={() => void handleCancel()}>
                {t("common:cancel")}
              </Button>
            )}
            <Button
              type={"submit"}
              isLoading={
                candidateActionCreate.isPending ||
                applicantActionCreate.isPending ||
                candidateActionUpdate.isPending ||
                isPending
              }
              leftIcon={<FontAwesomeIcon icon={"fa-light fa-paper-plane-top text-lg"} />}
              onClick={() => handleSave()}
            >
              {submitLabel ? submitLabel : t("common:save")}
            </Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
